// Angular
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  SimpleChanges,
  OnChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';

// Material
import { MatDateRangePicker } from '@angular/material/datepicker';

// Local
import { IFilterControl } from '../../../types/internal-interfaces';
import { DateRangeConfig } from './date-range-config';
import {
  DatePreset,
  DateRangeSelection,
} from '../../../shared/types/interfaces';

// Material
import { MatIconRegistry } from '@angular/material/icon';

// SAJ
import { SAJDatepickerHeaderComponent } from '@snagajob/angular/components/datepicker-header';

@Component({
  selector: 'date-range',
  templateUrl: 'date-range.component.html',
  styleUrls: ['date-range.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangeComponent implements OnInit, OnChanges, IFilterControl {
  @Input() configuration: DateRangeConfig;

  @Output() filterUpdated: EventEmitter<DateRangeSelection> =
    new EventEmitter();
  @Output() optionsSelectedCountUpdated: EventEmitter<number> =
    new EventEmitter();

  @ViewChild('picker') picker: MatDateRangePicker<any>;

  customDatepickerHeader = SAJDatepickerHeaderComponent;

  startDateControl = new UntypedFormControl();
  endDateControl = new UntypedFormControl();

  selectedOptionsCount: number = 0;
  selectedPresetIndex: number;

  dateRangeSelected: DateRangeSelection = {
    start: null,
    end: null,
  };

  readonly customDateRange: DatePreset = {
    label: 'Custom',
    start: null,
    end: null,
  };

  datePresets: DatePreset[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
  ) {
    this.iconRegistry.addSvgIcon(
      'saj-calendar',
      this.sanitizer.bypassSecurityTrustResourceUrl(
        'assets/images/icons/functional/calendar.svg',
      ),
    );
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges) {
    const hasChanges = this.hasPresetChanges(
      changes.configuration.currentValue,
      changes.configuration.previousValue,
    );
    if (hasChanges) {
      this.datePresets = [];
      changes.configuration.currentValue.datePresets.forEach(
        (preset: DatePreset) => {
          this.datePresets.push({ ...preset });
        },
      );

      if (this.datePresets.length) {
        // always add customDateRange to presets that are passed in.
        this.datePresets.push(this.customDateRange);
      }
    }
  }

  public setValue(currentOption: DateRangeSelection): void {
    this.setControlAndSelectedDates(currentOption?.start, currentOption?.end);
    this.selectMatchingPreset(
      new Date(currentOption?.start),
      new Date(currentOption?.end),
    );
    this.updateSelectedOptionsCount();
    this.optionsSelectedCountUpdated.emit(this.selectedOptionsCount);
    this.cdr.detectChanges();
  }

  public startDateChanged(date: Date): void {
    this.dateRangeSelected.start = date;
    this.selectCustomDateRange();
    this.processDateChange();
  }

  public endDateChanged(date: Date): void {
    this.dateRangeSelected.end = date;
    this.selectCustomDateRange();
    this.processDateChange();
  }

  public toggleFilter(preset: DatePreset, index: number) {
    this.selectedPresetIndex = index;

    const isCustomDateRangeSelected =
      index === this.datePresets.indexOf(this.customDateRange);
    if (isCustomDateRangeSelected) {
      // open calendar if selecting custom
      this.picker.open();
    } else {
      this.picker.close();
    }

    this.setControlAndSelectedDates(preset.start, preset.end);
    this.processDateChange();
  }

  public clear(): void {
    this.selectCustomDateRange();
    this.setControlAndSelectedDates(null, null);
    this.processDateChange();
  }

  private hasPresetChanges(
    current: DateRangeConfig,
    previous: DateRangeConfig,
  ): boolean {
    const changed = current.datePresets != previous?.datePresets;
    return changed;
  }

  private selectMatchingPreset(start: Date, end: Date): void {
    // go ahead and select the custom date range, then change it if there is a preset match
    this.selectCustomDateRange();

    for (let i = 0; i < this.datePresets.length; i++) {
      const preset = this.datePresets[i];

      const presetMatches =
        preset.start?.getTime() === start?.getTime() &&
        preset.end?.getTime() === end?.getTime();
      if (presetMatches) {
        this.selectedPresetIndex = i;
        break;
      }
    }
  }

  private selectCustomDateRange(): void {
    this.selectedPresetIndex = this.datePresets.indexOf(this.customDateRange);
  }

  private isEmptyRangeSelected(): boolean {
    return !this.dateRangeSelected.start && !this.dateRangeSelected.end;
  }

  private isValidDateRangeSelection(): boolean {
    const isEmptyRange = this.isEmptyRangeSelected();
    const isRangeSelected =
      !!this.dateRangeSelected.start && !!this.dateRangeSelected.end;
    return isEmptyRange || isRangeSelected;
  }

  private setControlAndSelectedDates(start: Date, end: Date): void {
    this.startDateControl.setValue(start);
    this.endDateControl.setValue(end);
    this.dateRangeSelected.start = start;
    this.dateRangeSelected.end = end;
  }

  private updateSelectedOptionsCount(): void {
    if (this.isValidDateRangeSelection()) {
      this.selectedOptionsCount = this.isEmptyRangeSelected() ? 0 : 1;
    } else {
      this.selectedOptionsCount = 0;
    }
  }

  private processDateChange(): void {
    if (this.isValidDateRangeSelection()) {
      this.dateRangeSelected.start = this.cleanDate(
        this.dateRangeSelected.start,
      );
      this.dateRangeSelected.end = this.cleanDate(this.dateRangeSelected.end);
      this.filterUpdated.emit({ ...this.dateRangeSelected });
    } else {
    }
    this.updateSelectedOptionsCount();
    this.optionsSelectedCountUpdated.emit(this.selectedOptionsCount);
  }

  private cleanDate(date: any): Date {
    return date;
  }
}
